'use client';

import { useState, useRef, useEffect } from 'react';
import { useNotifications, useUnreadCount, useMarkNotificationRead, useMarkAllNotificationsRead } from '@/hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';

export default function NotificationBell() {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: notifications = [], isLoading } = useNotifications();
  const { data: unreadData } = useUnreadCount();
  const markAsRead = useMarkNotificationRead();
  const markAllAsRead = useMarkAllNotificationsRead();

  const unreadCount = unreadData?.unread_count || 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notificationId: number, isRead: boolean) => {
    if (!isRead) {
      markAsRead.mutate(notificationId);
    }
    // TODO: Navigate to the referenced item (task, project, etc.)
  };

  const handleMarkAllRead = () => {
    markAllAsRead.mutate();
  };

  const getNotificationIcon = (type: number) => {
    switch (type) {
      case 1: // Status change
        return '📊';
      case 2: // Share/Assignment
        return '👤';
      case 3: // Due
        return '⏰';
      case 4: // New member
        return '✨';
      case 5: // Edit
        return '✏️';
      default:
        return '🔔';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 bg-[#1a1f3a] rounded-lg hover:bg-[#2d1b3d] transition-colors"
      >
        <svg
          className="w-5 h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-96 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg shadow-xl max-h-[32rem] overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2d1b3d]">
            <h3 className="text-white font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-sm text-orange-400 hover:text-orange-300 transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-[28rem]">
            {isLoading ? (
              <div className="p-4 text-gray-400 text-sm text-center">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-gray-400 text-sm text-center">
                No notifications
              </div>
            ) : (
              <div className="py-2">
                {notifications.map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification.id, notification.read)}
                    className={`w-full px-4 py-3 hover:bg-[#2d1b3d] text-left transition-colors border-b border-[#2d1b3d] last:border-b-0 ${
                      !notification.read ? 'bg-[#1f2542]' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Unread indicator dot */}
                      {!notification.read && (
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                      )}

                      <div className="flex-1 min-w-0">
                        {/* Message */}
                        <div className="text-white text-sm">
                          {notification.from_member && (
                            <span className="font-medium">
                              {`${notification.from_member.user.first_name || 'Unknown'} ${notification.from_member.user.last_name || 'User'}`.trim()}
                            </span>
                          )}{' '}
                          {notification.message}
                        </div>

                        {/* Metadata */}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">
                            {formatDistanceToNow(new Date(notification.date), { addSuffix: true })}
                          </span>
                          <span className="text-xs text-gray-600">•</span>
                          <span className="text-xs text-gray-500">
                            {getNotificationIcon(notification.type)} {notification.type_display}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
